import dayjs from 'dayjs'
import { Text } from '$components'
import {
  Button,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateValue,
  Heading,
  RangeCalendar,
} from 'react-aria-components'
import { ChevronLeftIcon } from '$components'
import { parseDate, CalendarDate, today, getLocalTimeZone } from '@internationalized/date'
import { cn } from '$components'
import { useState, useMemo } from 'react'
import { useFilters } from '../hooks/use-filters'
import { DateRangeFilterDefinition } from '../types'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export type DateRangeFilterOptionProps = {
  filter: DateRangeFilterDefinition
}

export function DateRangeFilterOption({ filter }: DateRangeFilterOptionProps) {
  const { setFilter, getFilter } = useFilters()
  const currentValue = getFilter(filter)

  const selectedRange = useMemo(() => {
    if (!currentValue) {
      return null
    }

    try {
      return {
        start: parseDate(dayjs.utc(currentValue.start).format('YYYY-MM-DD')),
        end: parseDate(dayjs.utc(currentValue.end).format('YYYY-MM-DD')),
      }
    } catch {
      return null
    }
  }, [currentValue])

  const [visibleDate, setVisibleDate] = useState<CalendarDate>(
    selectedRange?.start
      ? new CalendarDate(selectedRange.start.year, selectedRange.start.month, selectedRange.start.day)
      : today(getLocalTimeZone()),
  )

  const handleRangeChange = (range: { start: DateValue; end: DateValue } | null) => {
    if (range?.start && range?.end) {
      const startDate = dayjs.utc(range.start.toString())
      const endDate = dayjs.utc(range.end.toString())
      const rangeValue = {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      }
      setFilter(filter, rangeValue)
    } else {
      setFilter(filter, null)
    }
  }

  const handleMonthChange = (month: number) => setVisibleDate((prev: CalendarDate) => new CalendarDate(prev.year, month, 1))
  const handleYearChange = (year: number) => setVisibleDate((prev: CalendarDate) => new CalendarDate(year, prev.month, 1))

  return (
    <RangeCalendar
      aria-label={filter.label}
      value={selectedRange as unknown as { start: DateValue; end: DateValue }}
      onChange={handleRangeChange}
      focusedValue={visibleDate as unknown as DateValue}
      onFocusChange={(date) => {
        if (date && typeof date.year === 'number' && typeof date.month === 'number' && typeof date.day === 'number') {
          setVisibleDate(new CalendarDate(date.year, date.month, date.day))
        }
      }}
    >
      <header className="flex items-center justify-start sm:justify-center gap-stack-tight border-b border-border px-stack-roomy pt-stack-tight pb-stack-roomy sm:py-stack-tight">
        <Button
          slot="previous"
          className="p-stack-tight hover:bg-bg-subtle rounded-control text-fg-subtle"
          onPress={() => setVisibleDate((prev: CalendarDate) => prev.subtract({ months: 1 }))}
        >
          <ChevronLeftIcon className="size-4" />
        </Button>

        <div className="flex items-center gap-stack-tight">
          <Heading className="sr-only" />
          <MonthPicker
            value={visibleDate.month}
            onChange={handleMonthChange}
          />
          <YearPicker
            value={visibleDate.year}
            onChange={handleYearChange}
          />
        </div>

        <Button
          slot="next"
          className="p-stack-tight hover:bg-bg-subtle rounded-control text-fg-subtle"
          onPress={() => setVisibleDate((prev: CalendarDate) => prev.add({ months: 1 }))}
        >
          <ChevronLeftIcon className="rotate-180 size-4" />
        </Button>
      </header>

      <div className={cn({
        'p-stack-roomy': !selectedRange,
        'pt-stack-grid sm:pt-stack-roomy px-stack-roomy pb-stack-tight': selectedRange,
      })}
      >
        <CalendarGrid className="w-full">
          <CalendarGridHeader>
            {(day) => (
              <CalendarHeaderCell className="text-caption text-fg-subtle font-semibold pb-stack-tight text-center">
                {day}
              </CalendarHeaderCell>
            )}
          </CalendarGridHeader>
          <CalendarGridBody>
            {(date) => (
              <CalendarCell
                date={date}
                className={cn(
                  'size-8 outline-none cursor-default rounded-pill flex items-center justify-center text-body-sm text-fg-muted',
                  'hover:bg-bg-subtle focus-visible:border-2 border-border-focus mx-auto',
                  'data-[selected]:bg-fg data-[selected]:text-fg-inverse',
                  'data-[selection-start]:bg-fg data-[selection-start]:text-fg-inverse',
                  'data-[selection-end]:bg-fg data-[selection-end]:text-fg-inverse',
                  'data-[hovered]:bg-bg-subtle',
                  'data-[pressed]:bg-bg-subtle',
                  'data-[outside-month]:text-fg-subtle',
                  'data-[disabled]:!text-fg-disabled data-[disabled]:hover:!bg-transparent',
                  'data-[range-selection]:bg-bg-subtle',
                )}
              />
            )}
          </CalendarGridBody>
        </CalendarGrid>

        { selectedRange && (
          <div className="flex w-full pt-stack-tight justify-end">
            <button
              className="w-fit px-stack-roomy py-stack-tight sm:px-stack-tight sm:py-stack-tight focus:outline-none hover:bg-bg-subtle/50 rounded-control"
              onClick={() => handleRangeChange(null)}
            >
              <Text type="s">Clear selection</Text>
            </button>
          </div>
        )}
      </div>
    </RangeCalendar>
  )
}

type MonthPickerProps = {
  value: number
  onChange: (month: number) => void
}

function MonthPicker({ value, onChange }: MonthPickerProps) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ]

  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-body-sm font-medium text-fg-muted bg-transparent border-none outline-none cursor-pointer"
    >
      {months.map((month, index) => (
        <option
          key={month}
          value={index + 1}
        >
          {month}
        </option>
      ))}
    </select>
  )
}

type YearPickerProps = {
  value: number
  onChange: (year: number) => void
}

function YearPicker({ value, onChange }: YearPickerProps) {
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i)

  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-body-sm font-medium text-fg-muted bg-transparent border-none outline-none cursor-pointer"
    >
      {years.map((year) => (
        <option
          key={year}
          value={year}
        >
          {year}
        </option>
      ))}
    </select>
  )
}
